import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  type CritiqueColdReader,
  type CritiqueHardFailCode,
  type CritiqueKind,
  type CritiqueRecord,
  type CritiquesView,
  fetchCritiques,
  runCritique,
} from './api.ts';
import { DASHBOARD_POLL_MS, describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { localTime } from './schedule.ts';
import { Badge, Facts, Loader, Notice, Panel } from './ui.tsx';

/**
 * "Creative notes" (synthesis C1): what the independent critic said about
 * this content's words — another model family than the one that wrote them,
 * three strangers who each got one track of the ad, a judge with a scored
 * rubric, and the deterministic checks.
 *
 * The operator reads it in two places: while the planner writes the script
 * (the planner runs the critic before it asks for approval, and the notes are
 * here too), and at the sketch gate. The notes are advice: a hard fail never
 * holds **Approve scenes**.
 *
 * With `kind="draft"` it shows what the same critic said about the draft
 * video, before the final render (synthesis C8): the frame that stops the
 * scroll, whether a phone can read it, whether it moves like an ad, the look,
 * and whether the picture lands on the voice — and whether that sent the
 * composition back for another pass.
 *
 * With `kind="staging"` it is the **Staging** panel (US-009): how the scenes
 * are staged — a real place, coverage as a crew would shoot it, continuity,
 * purpose — scored by the same critic, with the staging checks. A plan that stages nobody
 * (an image, or only screen recordings and graphics) has no staging to judge,
 * and then there is no panel at all.
 */

/** How often the notes are read while a critique is being written. */
const RUNNING_POLL_MS = 4000;

/** The hard checks, as the operator reads them. */
const HARD_FAIL_LABEL: Record<CritiqueHardFailCode, string> = {
  no_hook: 'No hook',
  product_unclear_5s: 'Product not clear in 5 seconds',
  no_proof: 'No proof',
  no_ask: 'No clear ask',
  unsourced_claim: 'Claim without a source',
  cold_reader_wrong: 'A stranger got it wrong',
  words_mismatch: 'The words differ across the plan',
  dialogue_unvoiced: 'The characters speak, but nobody is heard',
  hook_frame: 'The first frame does not stop the scroll',
  unreadable: 'Cannot be read on a phone',
  slideshow: 'Moves like slides',
  motion_broken: 'A cut blinks, pops or stutters',
  look_unfinished: 'Looks unfinished',
  out_of_sync: 'Picture and voice apart',
  sound_broken: 'The sound does not work',
  frozen_people: 'A person is a still photo',
  no_establishing: 'Reverse shots before both people are seen together',
  isolated_portraits: 'One portrait per person, never together',
  crossed_line: 'The two people do not face each other',
  no_staging: 'People shown with no staging',
  prop_as_place: 'A prop stands in for the place',
  poor_coverage: 'The shots do not cover the action',
  continuity_broken: 'People, room or sides change between shots',
  house_rule_broken: 'A house rule is broken',
  line_unmotivated: 'A viewer cannot say why a line is said',
  payoff_without_setup: 'A payoff whose setup is never heard or shown',
  pronoun_without_noun: 'A pronoun with no noun before it',
  line_too_short: 'A line too short to be heard',
  banned_phrase: 'A line on the banned list',
  claim_unbacked: 'A claim the ledger does not back',
  cast_reference_missing: 'A cast character’s still without their reference',
  hook_repeated: 'A hook that repeats an earlier one',
  story_broken: 'A turn in the story that follows from nothing',
  joke_broken: 'The joke comes late, twice or as a gag',
  what_it_is_unclear: 'A stranger cannot say what it is',
  swap_test_failed: 'A line any competitor could say',
  outsider_word: 'A word the audience never uses',
  fiscal_detail_wrong: 'A fiscal or calendar detail that is wrong',
  claim_adjective: 'An adjective where proof belongs',
  claim_without_limit: 'A big claim without its limit',
  claim_contradicts: 'A claim another ad of the brand contradicts',
  replaces_person: 'Implies the product replaces a person',
  fear_without_action: 'A fear with nothing to do about it',
  gaze_missing: 'A person with nothing to look at',
  gaze_to_camera_untagged: 'A look into the lens not tagged to_camera',
};

/** The draft's criteria, in the order the critic scores them; continuity only when the plan has `## Staging`. */
const DRAFT_CRITERION_LABEL: readonly (readonly [string, string])[] = [
  ['hookFrame', 'The first frame'],
  ['legibility', 'Readable on a phone'],
  ['motion', 'Moves like an ad'],
  ['look', 'Bold and on-brand'],
  ['sync', 'Picture on the voice'],
  ['sound', 'The sound, as heard'],
  ['continuity', 'The same people in the same room'],
  ['people', 'People alive'],
];

/** The staging's four criteria, in the order the critic scores them. */
const STAGING_CRITERION_LABEL: readonly (readonly [string, string])[] = [
  ['situation', 'A real place, not a prop'],
  ['coverage', 'Covered as a crew would'],
  ['continuity', 'Same people, room and sides'],
  ['purpose', 'Every shot earns its place'],
];

/** What the critic was shown of the draft, in words. */
const WATCHED: Record<'video' | 'frames' | 'none', string> = {
  video: 'the video itself',
  frames: 'its frames and every cut',
  none: 'nothing: only the checks ran',
};

/** The rubric's thirteen criteria, in the order the critic scores them. */
const CRITERION_LABEL: readonly (readonly [string, string])[] = [
  ['hook', 'Hook'],
  ['clarity', 'Clear by 5 seconds'],
  ['message', 'One message'],
  ['proof', 'Proof'],
  ['differentiation', 'Only this product could say it'],
  ['relevance', 'The buyer’s words'],
  ['objection', 'Objection'],
  ['ask', 'The ask'],
  ['spoken', 'Written for the ear'],
  ['dualTrack', 'Works with sound off and screen off'],
  ['brand', 'Brand heard'],
  ['sync', 'Picture and words together'],
  ['tone', 'No ad-speak'],
];

const READER_LABEL: Record<CritiqueColdReader, string> = {
  voiceOnly: 'Heard the voice only',
  textOnly: 'Read the screen only',
  firstFiveSeconds: 'Scrolled on after 5 seconds',
};

export function CreativeNotesPanel({
  contentId,
  kind = 'script',
  atGate,
  busy,
}: {
  readonly contentId: string;
  /** The words (default), the draft video, or the staging. */
  readonly kind?: CritiqueKind;
  /** At the sketch gate, where the panel shows even before the first notes. */
  readonly atGate: boolean;
  /** Something else on the page is working; the buttons wait for it. */
  readonly busy: boolean;
}) {
  const draft = kind === 'draft';
  const staging = kind === 'staging';
  const [view, setView] = useState<CritiquesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal): void => {
      fetchCritiques(contentId, signal, kind)
        .then((next) => {
          setView(next);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setError(describeError(cause));
        });
    },
    [contentId, kind],
  );

  const running = view?.running !== null && view?.running !== undefined;
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = window.setInterval(
      () => {
        load(controller.signal);
      },
      running ? RUNNING_POLL_MS : DASHBOARD_POLL_MS * 5,
    );
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load, running]);

  const runAgain = (): void => {
    setWorking(true);
    setError(null);
    runCritique(contentId, kind)
      .then(setView)
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setWorking(false);
      });
  };

  // Nothing to say and nothing to judge yet: no panel at all. A draft panel
  // waits for the first draft the compose step renders.
  if (view !== null && view.latest === null && !running && view.problem !== null && (!atGate || draft)) return null;
  // No staging to judge (no plan yet, or one US-007 exempts): no panel, even
  // with notes on an earlier staging. Nor a loader before the first read, so
  // an exempt plan does not flash one.
  if (staging && (view === null || view.problem !== null)) return null;

  const record: CritiqueRecord | null = view?.current ?? view?.latest ?? null;
  const result = record?.result ?? null;

  return (
    <Panel
      id={draft ? 'creative-notes-draft' : staging ? 'creative-notes-staging' : 'creative-notes'}
      title={draft ? 'Creative notes on the draft' : staging ? 'Staging' : 'Creative notes'}
      icon="comment"
      meta={
        running ? (
          <Badge tone="active" pulse>
            reading
          </Badge>
        ) : result === null ? undefined : (
          <Badge tone={result.verdict === 'pass' ? 'done' : 'danger'}>
            {result.verdict === 'pass' ? 'pass' : `${String(result.hardFails.length)} hard fail${result.hardFails.length === 1 ? '' : 's'}`}
          </Badge>
        )
      }
      actions={
        <button
          type="button"
          className="button button--small"
          onClick={runAgain}
          disabled={working || busy || running || view === null || !view.available || view.problem !== null}
          title={
            view !== null && !view.available
              ? (view.note ?? undefined)
              : draft
                ? 'Watch the latest draft again'
                : staging
                  ? 'Judge the staging as it stands now'
                  : 'Judge the words as they stand now'
          }
        >
          <Icon name="sync" />
          {record === null ? 'Run the critic' : 'Run again'}
        </button>
      }
    >
      {draft ? (
        <p className="muted">
          The same critic watches the draft before the final render: the first frame, whether a phone can read it,
          how it moves, the look, and whether the picture lands on the voice. A hard fail sends it back for one more
          pass; after that, the notes are yours to weigh.
        </p>
      ) : staging ? (
        <p className="muted">
          The critic reads how the scenes are staged: a real place, shots a film crew would take, the same people on
          the same sides, and a reason for every shot.
        </p>
      ) : (
        <p className="muted">
          An independent critic reads the words the way a stranger in the feed would, then scores them.
        </p>
      )}
      {error !== null && <Notice kind="error">{error}</Notice>}
      {view === null ? (
        <Loader label="Reading the notes…" size="sm" />
      ) : (
        <>
          {view.problem !== null && <p className="muted">{view.problem}</p>}
          {view.note !== null && <Notice kind="info">{view.note}</Notice>}
          {running && (
            <Loader
              label={
                draft
                  ? 'The critic is watching the draft'
                  : staging
                    ? 'The critic is reading the staging'
                    : 'The critic is reading the words'
              }
              detail={
                draft
                  ? 'It measures how the draft moves, then watches it; this usually takes a minute or two.'
                  : staging
                    ? 'The staging checks and a judge; this usually takes a minute.'
                    : 'Three cold readers and a judge; this usually takes a minute or two.'
              }
              size="sm"
            />
          )}
          {record !== null && !record.current && view.currentHash !== null && (
            <Notice kind="warn">
              {draft
                ? 'These notes are about an earlier draft. Run the critic again for the latest one.'
                : staging
                  ? 'These notes are about an earlier staging. Run the critic again for the scenes as they stand.'
                  : 'These notes are about an earlier version of the words. Run the critic again for the words as they stand.'}
            </Notice>
          )}
          {draft && result?.draft !== undefined && (
            <p className="muted">
              The critic saw {WATCHED[result.draft.watched]}.
              {result.draft.sentBack ? ' Its hard fails sent the composition back for one more pass.' : ''}
            </p>
          )}
          {record !== null && record.status === 'failed' && (
            <Notice kind="error">The last critique could not be finished: {record.error ?? 'no reason was recorded.'}</Notice>
          )}
          {result !== null && <CritiqueBody record={record} />}
        </>
      )}
    </Panel>
  );
}

/** The critique itself: summary, hard fails, the three strangers, scores, rewrites, the checks. */
function CritiqueBody({ record }: { readonly record: CritiqueRecord | null }) {
  const result = record?.result ?? null;
  if (record === null || result === null) return null;
  const warnings = result.lint.filter((finding) => finding.severity === 'warn');
  const draft = result.kind === 'draft';
  const staging = result.kind === 'staging';
  const scored = (draft ? DRAFT_CRITERION_LABEL : staging ? STAGING_CRITERION_LABEL : CRITERION_LABEL).filter(([key]) => result.scores[key] !== undefined);
  const total = scored.reduce((sum, [key]) => sum + (result.scores[key]?.score ?? 0), 0);
  return (
    <>
      <p className="review__verdict">{result.summary}</p>

      {result.hardFails.length > 0 && (
        <div className="review__group">
          <div className="review__group-title">Hard fails</div>
          <ul className="review__findings">
            {result.hardFails.map((fail, index) => (
              <li className="review__finding review__finding--block" key={`${fail.code}-${String(index)}`}>
                <div className="review__meta">
                  <span>{HARD_FAIL_LABEL[fail.code]}</span>
                  {fail.where !== undefined && <span>{fail.where}</span>}
                </div>
                <div className="review__summary">{fail.message}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.kind === 'script' && (
        <div className="review__group">
          <div className="review__group-title">What a stranger took from it</div>
          <ul className="review__findings">
            {(Object.keys(READER_LABEL) as CritiqueColdReader[]).map((reader) => {
              const reading = result.coldReader[reader];
              if (reading.product === '' && reading.correct === null) return null;
              return (
                <li
                  className={`review__finding${reading.correct === false ? ' review__finding--block' : ''}`}
                  key={reader}
                >
                  <div className="review__meta">
                    <span>{READER_LABEL[reader]}</span>
                    {reading.correct !== null && (
                      <Badge tone={reading.correct ? 'done' : 'danger'}>{reading.correct ? 'got it' : 'got it wrong'}</Badge>
                    )}
                  </div>
                  <div className="review__summary">{reading.product}</div>
                  <div className="review__detail">
                    {reading.whatItDoes} · {reading.benefit} · Ask: {reading.ask}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {result.rewrites.length > 0 && (
        <div className="review__group">
          <div className="review__group-title">{draft || staging ? 'What to change' : 'Suggested rewrites'}</div>
          <ul className="review__findings">
            {result.rewrites.map((rewrite, index) => (
              <li className="review__finding review__finding--warning" key={String(index)}>
                <div className="review__meta">
                  <span>{rewrite.where}</span>
                </div>
                <div className="review__detail">
                  <s>{rewrite.before}</s>
                </div>
                <div className="review__summary">{rewrite.after}</div>
                <div className="review__detail">{rewrite.why}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {scored.length > 0 && (
        <details className="review__checked" open={staging}>
          <summary>
            Scores: {total} of {scored.length * 3}
          </summary>
          <ul className="review__checked-list">
            {scored.map(([key, label]) => (
              <li key={key}>
                <strong>
                  {label}: {result.scores[key]?.score}/3
                </strong>{' '}
                — {result.scores[key]?.note}
              </li>
            ))}
          </ul>
        </details>
      )}

      {warnings.length > 0 && (
        <details className="review__checked" open={staging}>
          <summary>
            {warnings.length} note{warnings.length === 1 ? '' : 's'} from the checks
          </summary>
          <ul className="review__checked-list">
            {warnings.map((finding, index) => (
              <li key={`${finding.code}-${String(index)}`}>
                {finding.where === undefined ? '' : `${finding.where}: `}
                {finding.message}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Facts
        items={[
          { label: 'Judged by', value: result.model, mono: true },
          { label: 'When', value: localTime(record.createdAt) },
          { label: 'Cost', value: `$${record.costUsd.toFixed(2)}` },
        ]}
      />
    </>
  );
}
